/**
 * The donor's own promises, and a way to pay each one (GH-809).
 *
 * The web could make a pledge and never pay one: a promise made at an event had no way home.
 * This is the web's half of the iOS wallet's "Promises" (`/v1/donor/pledges`), drawn for a
 * signed-in donor on the home screen.
 *
 * ## What a row says, and what it never says
 *
 * What is still owed — the server's `outstanding`, never a subtraction made here — and who it was
 * promised to. Never a balance, never "received", and never added to anything: a pledge is not
 * money (CLAUDE.md). A promise already kept in full is not listed (`fetchDonorPledges`).
 *
 * ## Paying
 *
 * "Pay pledge" mints a checkout for the pledge's event and opens it with `?pledge=<id>`. The id is
 * a hint: checkout reads the donor's own pledge list to confirm it, and the quote carries it with
 * the donor's session, where the server checks it is theirs, still open, and for that event. The
 * event's state does not matter (owner, 2026-10-01): only an organization that can no longer
 * accept donations refuses, and the mint says so in the server's words.
 */
import { useEffect, useState } from "react";
import { formatMoney, type DonorPledge } from "@givehere/contracts";
import { ApiError, fetchDonationLink } from "./api.js";
import { fetchDonorPledges } from "./donor-session.js";

export function DonorPledges({ token }: { token: string }) {
  const [pledges, setPledges] = useState<readonly DonorPledge[]>([]);
  const [opening, setOpening] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ pledgeId: string; message: string } | null>(null);

  useEffect(() => {
    let live = true;
    fetchDonorPledges(token).then(
      (loaded) => live && setPledges(loaded),
      // An optional section: a read that fails leaves the directory exactly as it was.
      () => live && setPledges([]),
    );
    return () => {
      live = false;
    };
  }, [token]);

  if (pledges.length === 0) return null;

  const pay = (pledge: DonorPledge) => {
    setOpening(pledge.pledgeId);
    setFailure(null);
    fetchDonationLink(pledge.eventId)
      .then((link) =>
        window.location.assign(
          `/g/${encodeURIComponent(link.token)}?pledge=${encodeURIComponent(pledge.pledgeId)}`,
        ),
      )
      .catch((caught: unknown) => {
        setOpening(null);
        setFailure({
          pledgeId: pledge.pledgeId,
          message: caught instanceof ApiError ? caught.message : "That didn't open — try again.",
        });
      });
  };

  return (
    <section className="pledges" aria-labelledby="pledges-heading">
      <h2 id="pledges-heading" className="home__section">
        Your pledges
      </h2>
      <ul className="pledges__list">
        {pledges.map((pledge) => (
          <li key={pledge.pledgeId} className="pledges__row">
            <span className="pledges__text">
              <span className="pledges__event" dir="auto">
                {pledge.eventName}
              </span>
              <span className="pledges__meta">
                <span dir="auto">{pledge.organizationDisplayName}</span>
                {` · ${formatMoney(pledge.outstanding)} to pay`}
              </span>
            </span>
            <button
              type="button"
              className="pay pay--compact"
              disabled={opening !== null}
              aria-label={`Pay pledge to ${pledge.organizationDisplayName}, ${formatMoney(pledge.outstanding)}`}
              onClick={() => pay(pledge)}
            >
              {opening === pledge.pledgeId ? "Opening…" : "Pay pledge"}
            </button>
            {failure?.pledgeId === pledge.pledgeId && (
              <p className="notice notice--error pledges__failure" role="alert">
                {failure.message}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
